import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import logoWhiteImg from "@/assets/logo-white.svg";
import { recordAuthEvent } from "@/lib/audit-log.functions";
import { resetPasswordWithRecoveryCode } from "@/lib/mfa-recovery.functions";
import { PasswordStep } from "@/components/reset-password/PasswordStep";
import { MfaStep } from "@/components/reset-password/MfaStep";
import { RecoveryStep } from "@/components/reset-password/RecoveryStep";
import { DoneStep } from "@/components/reset-password/DoneStep";
import { validateNewPassword } from "@/lib/password-strength";

export const Route = createFileRoute("/reset-password")({
  head: () => ({
    meta: [
      { title: "Nulstil adgangskode - AutoXChange" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: ResetPassword,
});

type Step = "password" | "mfa" | "recovery" | "done";

function ResetPassword() {
  const navigate = useNavigate();
  const recordEvent = useServerFn(recordAuthEvent);
  const recoveryReset = useServerFn(resetPasswordWithRecoveryCode);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<Step>("password");
  const [validSession, setValidSession] = useState(false);
  const [checking, setChecking] = useState(true);
  const [linkErrorMessage, setLinkErrorMessage] = useState<string | null>(null);

  // MFA state
  const [mfaFactorId, setMfaFactorId] = useState<string | null>(null);
  const [mfaChallengeId, setMfaChallengeId] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState("");
  const [pendingPassword, setPendingPassword] = useState("");
  const [recoveryCode, setRecoveryCode] = useState("");
  const [remainingRecoveryCodes, setRemainingRecoveryCodes] = useState<number | null>(null);

  const logCompleted = async (mfaUsed: boolean) => {
    try {
      const { data: aalData } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      await recordEvent({
        data: {
          eventType: "password_reset_completed",
          description: mfaUsed ? "Password reset gennemført med MFA" : "Password reset gennemført",
          metadata: {
            mfa_used: mfaUsed,
            aal: aalData?.currentLevel ?? null,
          },
        },
      });
    } catch (err) {
      console.error("[reset-password] audit-log fejlede:", err);
    }
  };

  useEffect(() => {
    // Parse Supabase error params from URL hash (e.g. expired/invalid OTP).
    if (typeof window !== "undefined" && window.location.hash) {
      const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));
      const errCode = hash.get("error_code") || hash.get("error");
      const errDesc = hash.get("error_description");
      if (errCode) {
        const friendly =
          errCode === "otp_expired" || errDesc?.includes("expired")
            ? "Linket er udløbet. Anmod om et nyt nulstillingslink."
            : errCode === "access_denied"
              ? "Linket er ugyldigt eller er allerede brugt. Anmod om et nyt."
              : (errDesc ? decodeURIComponent(errDesc.replace(/\+/g, " ")) : "Linket er ikke gyldigt. Anmod om et nyt.");
        setLinkErrorMessage(friendly);
        setChecking(false);
        // Strip the hash so reload doesn't show the same error
        try { window.history.replaceState(null, "", window.location.pathname); } catch { /* ignore */ }
        return;
      }
    }

    // CRITICAL: only treat the session as a valid recovery session if Supabase
    // emitted PASSWORD_RECOVERY (i.e. user arrived via the email link) OR the
    // URL still carries the recovery hash. A pre-existing logged-in session
    // must NOT allow password change without re-authentication.
    let recoveryConfirmed = false;
    const hasRecoveryHashOnLoad =
      typeof window !== "undefined" &&
      window.location.hash.includes("type=recovery");

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY" && session) {
        recoveryConfirmed = true;
        setValidSession(true);
        setChecking(false);
      }
    });

    // Give Supabase a brief window to process the recovery token from the URL
    // and emit PASSWORD_RECOVERY. If we still have no recovery confirmation
    // after that, reject — even if a normal session exists.
    const timeout = setTimeout(async () => {
      if (recoveryConfirmed) return;
      if (hasRecoveryHashOnLoad) {
        // Supabase processed the token but didn't fire the event yet — recheck.
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          setValidSession(true);
          setChecking(false);
          return;
        }
      }
      setValidSession(false);
      setChecking(false);
    }, 1500);

    return () => {
      clearTimeout(timeout);
      subscription.unsubscribe();
    };
  }, []);
  // Sign the user out of all sessions/devices and forward to login. Doing this
  // after a password reset is the secure default — even if the recovery was
  // triggered by an attacker who already had a session, the new password
  // invalidates them on next sign-in.
  const finishResetAndExit = async (mfaUsed: boolean, redirectMs: number) => {
    void logCompleted(mfaUsed);
    try {
      await supabase.auth.signOut({ scope: "global" });
    } catch (err) {
      console.error("[reset-password] global signOut fejlede:", err);
    }
    setStep("done");
    setTimeout(
      () =>
        navigate({
          to: "/login",
          search: { redirect: "/admin" } as never,
        }),
      redirectMs,
    );
  };


  const beginMfaIfNeeded = async (): Promise<{ needsMfa: boolean; error?: string }> => {
    const [aalRes, factorRes] = await Promise.all([
      supabase.auth.mfa.getAuthenticatorAssuranceLevel(),
      supabase.auth.mfa.listFactors(),
    ]);

    if (aalRes.error) return { needsMfa: false, error: "Kunne ikke verificere session-niveau" };
    if (factorRes.error) return { needsMfa: false, error: "Kunne ikke hente MFA-faktorer" };

    const verifiedFactor = (factorRes.data?.totp ?? []).find((f) => f.status === "verified");
    if (!verifiedFactor || aalRes.data?.currentLevel === "aal2") {
      return { needsMfa: false };
    }

    const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
      factorId: verifiedFactor.id,
    });
    if (challengeError || !challengeData) {
      return { needsMfa: true, error: challengeError?.message || "Kunne ikke starte MFA-verifikation" };
    }
    setMfaFactorId(verifiedFactor.id);
    setMfaChallengeId(challengeData.id);
    return { needsMfa: true };
  };

  const updatePassword = async (newPassword: string): Promise<string | null> => {
    const { error: updateError } = await supabase.auth.updateUser({ password: newPassword });
    if (!updateError) return null;

    const msg = updateError.message?.toLowerCase() ?? "";
    const code = (updateError as { code?: string }).code;

    if (msg.includes("aal2") || msg.includes("mfa")) {
      return "Din konto kræver to-faktor godkendelse for at ændre adgangskoden. Indtast koden fra din authenticator-app.";
    }
    if (code === "weak_password" || msg.includes("pwned") || msg.includes("leaked") || msg.includes("weak")) {
      return "Adgangskoden er for svag eller er fundet i kendte datalæk. Vælg en længere og mere unik adgangskode (mindst 8 tegn, gerne en sætning).";
    }
    if (msg.includes("same") || msg.includes("different from the old")) {
      return "Den nye adgangskode skal være forskellig fra den nuværende.";
    }
    return updateError.message || "Kunne ikke opdatere adgangskoden. Prøv igen.";
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    const strengthError = validateNewPassword(password);
    if (strengthError) {
      setError(strengthError);
      return;
    }
    if (password !== confirmPassword) {
      setError("Adgangskoderne stemmer ikke overens");
      return;
    }

    setLoading(true);

    const mfaCheck = await beginMfaIfNeeded();
    if (mfaCheck.error && !mfaCheck.needsMfa) {
      setError(mfaCheck.error);
      setLoading(false);
      return;
    }
    if (mfaCheck.needsMfa) {
      if (mfaCheck.error) {
        setError(mfaCheck.error);
        setLoading(false);
        return;
      }
      setPendingPassword(password);
      setStep("mfa");
      setLoading(false);
      return;
    }

    const updateErr = await updatePassword(password);
    setLoading(false);
    if (updateErr) {
      setError(updateErr);
      return;
    }
    void finishResetAndExit(false, 2000);
  };

  const handleMfaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!mfaFactorId || !mfaChallengeId) {
      setError("MFA-session mangler — start forfra");
      return;
    }
    if (!/^\d{6}$/.test(mfaCode.trim())) {
      setError("Indtast den 6-cifrede kode fra din authenticator-app");
      return;
    }

    setLoading(true);
    const { error: verifyError } = await supabase.auth.mfa.verify({
      factorId: mfaFactorId,
      challengeId: mfaChallengeId,
      code: mfaCode.trim(),
    });

    if (verifyError) {
      setLoading(false);
      const { data: newChallenge } = await supabase.auth.mfa.challenge({ factorId: mfaFactorId });
      if (newChallenge) setMfaChallengeId(newChallenge.id);
      setMfaCode("");
      setError(
        verifyError.message === "Invalid TOTP code entered"
          ? "Forkert kode — prøv igen med en frisk kode"
          : verifyError.message || "MFA-verifikation mislykkedes",
      );
      return;
    }

    const updateErr = await updatePassword(pendingPassword);
    setLoading(false);
    if (updateErr) {
      setError(updateErr);
      return;
    }
    setPendingPassword("");
    void finishResetAndExit(true, 2000);
  };

  const handleRecoverySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    const newPassword = pendingPassword || password;
    const strengthError = validateNewPassword(newPassword);
    if (strengthError) {
      setError(strengthError);
      return;
    }
    if (!recoveryCode.trim()) {
      setError("Indtast din recovery-kode");
      return;
    }

    setLoading(true);
    try {
      const result = await recoveryReset({
        data: { code: recoveryCode.trim(), newPassword },
      });
      setPendingPassword("");
      setRecoveryCode("");
      const remaining = result?.remaining ?? 0;
      setRemainingRecoveryCodes(remaining);
      setStep("done");
      // Skip auto-redirect når koderne er lave så brugeren kan klikke "Generér nye"
      if (remaining > 2) {
        setTimeout(() => navigate({ to: "/login", search: { redirect: "/admin" } as never }), 4000);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Kunne ikke nulstille adgangskoden";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-dark-blue">
        <div className="animate-spin h-8 w-8 border-2 border-accent border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!validSession || linkErrorMessage) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-dark-blue px-4">
        <div className="w-full max-w-sm text-center">
          <img src={logoWhiteImg} alt="AutoXChange" className="mx-auto h-12 w-auto" />
          <h1 className="mt-6 text-lg font-semibold text-white">Ugyldigt eller udløbet link</h1>
          <p className="mt-2 text-sm text-white/70">
            {linkErrorMessage ?? "Dette nulstillingslink er ikke længere gyldigt. Anmod om et nyt."}
          </p>
          <Button
            onClick={() => navigate({ to: "/login" })}
            className="mt-6 bg-accent text-accent-foreground hover:brightness-110"
          >
            Anmod om nyt link
          </Button>
        </div>
      </div>
    );
  }

  if (step === "done") {
    return <DoneStep remainingRecoveryCodes={remainingRecoveryCodes} />;
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-dark-blue px-4">
      <div className="w-full max-w-sm">
        <div className="text-center">
          <img src={logoWhiteImg} alt="AutoXChange" className="mx-auto h-12 w-auto" />
          <p className="mt-2 text-sm text-white/70">
            {step === "password"
              ? "Vælg en ny adgangskode"
              : step === "mfa"
                ? "Bekræft med to-faktor godkendelse"
                : "Nulstil med recovery-kode"}
          </p>
        </div>

        {step === "password" && (
          <PasswordStep
            password={password}
            confirmPassword={confirmPassword}
            error={error}
            loading={loading}
            onPasswordChange={setPassword}
            onConfirmPasswordChange={setConfirmPassword}
            onSubmit={handlePasswordSubmit}
          />
        )}

        {step === "mfa" && (
          <MfaStep
            code={mfaCode}
            error={error}
            loading={loading}
            onCodeChange={setMfaCode}
            onSubmit={handleMfaSubmit}
            onSwitchToRecovery={() => {
              setError("");
              setMfaCode("");
              setStep("recovery");
            }}
          />
        )}

        {step === "recovery" && (
          <RecoveryStep
            code={recoveryCode}
            error={error}
            loading={loading}
            onCodeChange={setRecoveryCode}
            onSubmit={handleRecoverySubmit}
            onSwitchToMfa={() => {
              setError("");
              setRecoveryCode("");
              setStep("mfa");
            }}
          />
        )}
      </div>
    </div>
  );
}
